import React, { useState, useEffect, useRef } from 'react';
import { useUserProfile } from '../../helper/UserProfile';
import { useLocation } from "react-router-dom";
import { useRevalidationData } from '../../helper/UserRevalidationProgress';
import '../../stylesheets/revalidation.scss';
import supabase from '../../helper/supabaseClient';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import { downloadPHLExcelDirect } from '../../helper/PHLExcelDownload';
import MobileRevalNav from '../mobile/MobileRevalNav';

function PHL() {
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [date, setDate] = useState("");
    const [nameAddress, setNameAddress] = useState("");
    const [workSetting, setWorkSetting] = useState("");
    const [scope, setScope] = useState("");
    const [hours, setHours] = useState("");
    const [weeks, setWeeks] = useState("");
    const [description, setDescription] = useState("");
    const [isEditing, setIsEditing ] = useState(false);
    const [editId, setEditId] = useState("");
    const [selectedQualifications, setSelectedQualifications] = useState([]);
    const [selectedQualificationsForPhl, setSelectedQualificationsForPhl] = useState([]);

    const { profile, loading } = useUserProfile();

    const { phlHours, phl, refresh } = useRevalidationData();

    const location = useLocation();
    const activeRef = useRef(null);

    useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [location.pathname]); // run on route change



    //Fetch User Qualifications
    const fetchMemberQualifications = async () => {
        if (!profile?.reval_members?.id) return;

        const { data, error } = await supabase
            .from('reval_member_qualifications')
            .select('qualification_id, reval_qualification(id, qualification)')
            .eq('reval_member_id', profile.reval_members.id);

        if (error) {
            console.error("Error fetching member qualifications", error);
            return;
        }

        const options = data.map((item) => ({
            value: item.qualification_id,
            label: item.reval_qualification.qualification,
        }));

        setSelectedQualifications(options); // Used in the dropdown for PHL entries
    };


    useEffect(() => {
        if (profile?.id) {
            fetchMemberQualifications();
            setError("");
        }
    }, [profile]);

    //HandleSubmit, Edits and Deletes
    const handleSubmit = async (e) => {
    e.preventDefault();

    if (!profile?.id) {
        setError("User not loaded. Please wait and try again.");
        return;
    }

    if (isEditing && editId) {
    const { error } = await supabase
      .from('phl_entries')
      .update({
        date,
        name_address: nameAddress,
        work_setting: workSetting,
        scope_of_practice: scope,
        hours_per_week: hours,
        number_weeks: weeks,
        registration: selectedQualificationsForPhl.map(q => q.label).join(', '),
        description,
      })
      .eq('id', editId);

    if (error) {
      setError("Could not update entry");
      return;
    }
    setSuccess("Entry updated");
    refresh();
    setIsEditing(false);
    setEditId(null);
    } else {
        const { data, error } = await supabase
        .from('phl_entries')
        .insert({
            date,
            name_address: nameAddress,
            work_setting: workSetting,
            scope_of_practice: scope,
            hours_per_week: hours,
            number_weeks: weeks,
            description,
            registration: selectedQualificationsForPhl.map(q => q.label).join(', '),
            user_id: profile.id,
        });

        if (error) {
        setError("There has been an issue");
        setSuccess("");
        return;
        }
        refresh();
        setSuccess("Your log has been updated");
    }

    // Reset form
    setError("");
    setDate("");
    setHours("");
    setNameAddress("");
    setWorkSetting("");
    setWeeks("");
    setScope("");
    setDescription("");
    setSelectedQualificationsForPhl("");
    };

    const handleDelete = async (id) => {
        const { error } = await supabase
        .from('phl_entries')
        .delete()
        .eq('id', id);

    if (error) {
        setError("Could not delete entry");
    } else {
        setSuccess("Entry deleted");
    }
    };
    
    const populateFormForEdit = (entry) => {
        setDate(entry.date);
        setHours(entry.hours_per_week);
        setNameAddress(entry.name_address);
        setWorkSetting(entry.work_setting);
        setScope(entry.scope_of_practice);
        setWeeks(entry.number_weeks);
        setDescription(entry.description);
        setEditId(entry.id);
        setIsEditing(true);

        const labels = entry.registration?.split(',').map(label => label.trim());

        const matchingOptions = selectedQualifications.filter(q => labels.includes(q.label));

        setSelectedQualificationsForPhl(matchingOptions);
        };

  return (
    <div>
        <div className="mob-only">
            <MobileRevalNav />
        </div>
        <div className="dashboard-body">
            <h1>PRACTICE HOURS LOG</h1>
            <p>Please fill in the form for each of your periods of practice. You must reach 450 hours per qualification. Please note that your qualification is based on your Revalidation Profile; please ensure this is updated to enable correct options.</p>
            <p>For example, if you are both a nurse and a midwife, you will need to provide information to cover a total of 900 hours of practice (450 hours for each qualification).</p>

            {/*Desktop*/}
            <form onSubmit = { handleSubmit } className="desktop-only">
                <table className="phl reval-input">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Hours Per Week</th>
                            <th>Number of Weeks</th>
                            <th>Qualification</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><input type="date" value={date} onChange={(e) =>setDate(e.target.value)}></input></td>
                            <td><input type="text" value={hours}onChange={(e) => setHours(e.target.value)}></input></td>
                            <td><input type="text" value={weeks} onChange={(e) => setWeeks(e.target.value)}></input></td>
                            <td><Select
                                    isMulti
                                    options={selectedQualifications}
                                    value={selectedQualificationsForPhl}
                                    onChange={(selected) => setSelectedQualificationsForPhl(selected)}
                                    placeholder="Select..."
                                    className="reval-dropdown"
                                    classNamePrefix="rs"
                                    styles={{
                                        control: (base) => ({
                                        ...base,
                                        minHeight: '26px',
                                        fontSize: '0.9rem',
                                        }),
                                        valueContainer: (base) => ({
                                        ...base,
                                        padding: '0 6px',
                                        }),
                                        indicatorsContainer: (base) => ({
                                        ...base,
                                        height: '26px',
                                        }),
                                        multiValue: (base) => ({
                                        ...base,
                                        fontSize: '0.85rem',
                                        }),}}
                                /></td>
                        </tr>
                    </tbody>
                </table>
                <br></br>
                <table className="phl reval-input">
                    <thead>
                        <tr>
                            <th>Name & Address of <br></br>Organisation</th>
                            <th>Work Setting</th>
                            <th>Scope of Practice</th>
                            <th>Brief Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><textarea value={nameAddress} onChange={(e) => setNameAddress(e.target.value)}></textarea></td>
                            <td><textarea value={workSetting} onChange={(e) => setWorkSetting(e.target.value)}></textarea></td>
                            <td><textarea value={scope} onChange={(e) => setScope(e.target.value)}></textarea></td>
                            <td><textarea value={description} onChange={(e) => setDescription(e.target.value)}></textarea></td>
                        </tr>
                    </tbody>
                </table>
                <br></br>
                {isEditing === false ? <button className="reval" type="submit" disabled={!profile?.id || loading}>Submit</button> : <button className="reval" type="submit">Update</button>}
                {error && <p className="error-message"><br></br>{error}</p>}
                {success && <p className="success"><br></br>{success}</p>}
            </form>

            {/*Mobile*/}
            <form onSubmit = { handleSubmit } className="mob-only">
                <table className="phl reval-input">
                    <tr>
                        <th>Date</th>
                        <td><input type="date" value={date} onChange={(e) =>setDate(e.target.value)}></input></td>
                    </tr>
                    <tr>
                        <th>Hours Per Week</th>
                        <td><input type="text" value={hours}onChange={(e) => setHours(e.target.value)}></input></td>
                    </tr>
                    <tr>
                        <th>Number of Weeks</th>
                        <td><input type="text" value={weeks} onChange={(e) => setWeeks(e.target.value)}></input></td>
                    </tr>
                    <tr>
                        <th>Qualification</th>
                        <td><Select
                                    isMulti
                                    options={selectedQualifications}
                                    value={selectedQualificationsForPhl}
                                    onChange={(selected) => setSelectedQualificationsForPhl(selected)}
                                    placeholder="Select..."
                                    className="reval-dropdown"
                                    classNamePrefix="rs"
                                    styles={{
                                        control: (base) => ({
                                        ...base,
                                        minHeight: '26px',
                                        fontSize: '0.9rem',
                                        }),
                                        valueContainer: (base) => ({
                                        ...base,
                                        padding: '0 6px',
                                        }),
                                        indicatorsContainer: (base) => ({
                                        ...base,
                                        height: '26px',
                                        }),
                                        multiValue: (base) => ({
                                        ...base,
                                        fontSize: '0.85rem',
                                        }),}}
                                /></td>
                    </tr>
                    <tr>
                        <th>Name & Address of Organisation</th>
                        <td><textarea value={nameAddress} onChange={(e) => setNameAddress(e.target.value)}></textarea></td>
                    </tr>
                    <tr>
                        <th>Work Setting</th>
                        <td><textarea value={workSetting} onChange={(e) => setWorkSetting(e.target.value)}></textarea></td>
                    </tr>
                    <tr>
                        <th>Scope of Practice</th>
                        <td><textarea value={scope} onChange={(e) => setScope(e.target.value)}></textarea></td>
                    </tr>
                    <tr>
                        <th>Brief Description</th>
                        <td><textarea value={description} onChange={(e) => setDescription(e.target.value)}></textarea></td>
                    </tr>
                </table>
                
                <br></br>
                {isEditing === false ? <button className="reval" type="submit" disabled={!profile?.id || loading}>Submit</button> : <button className="reval" type="submit">Update</button>}
                {error && <p className="error-message"><br></br>{error}</p>}
                {success && <p className="success"><br></br>{success}</p>}
            </form>


            <br></br>
            <h3>YOUR EXISTING LOG</h3>

            <p>{phlHours === "0" ? "Your log is empty. Please log your hours above." : `You have currently logged ${phlHours} hours.`}</p>
            
            <table className="phl reval-log desktop-only">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Name & Address <br></br>of Organisation</th>
                        <th>Work Setting</th>
                        <th>Scope of practice</th>
                        <th>Hours per week</th>
                        <th>Number of weeks</th>
                        <th>Total Hours</th>
                        <th>Registration</th>
                        <th>Brief description</th>
                        <th style={{width:"1rem", backgroundColor:"#fff"}}></th>
                        <th style={{width:"1rem", backgroundColor:"#fff"}}></th>
                    </tr>
                </thead>
                <tbody>
            {phl.map((entry, index) => (
                <tr key={index}>
                <td>{entry.date}</td>
                <td>{entry.name_address}</td>
                <td>{entry.work_setting}</td>
                <td>{entry.scope_of_practice}</td>
                <td>{entry.hours_per_week}</td>
                <td>{entry.number_weeks}</td>
                <td>{entry.total_hours}</td>
                <td>{entry.registration}</td>
                <td>{entry.description}</td>
                <td><FontAwesomeIcon icon={faPenToSquare} style={{fontSize:"0.8rem"}} onClick={() => populateFormForEdit(entry)}/></td>
                <td><FontAwesomeIcon icon={faTrashCan} style={{fontSize:"0.8rem"}} onClick={() => handleDelete(entry.id)}/></td>
                </tr>
            ))}
            </tbody>
            </table>

            <table className="phl reval-log mob-only">
                <thead>
                    <th style={{width: "90px"}}>Date</th>
                    <th>Organisation</th>
                    <th style={{width: "50px"}}>Total Hours</th>
                    <th style={{width:"1rem", backgroundColor:"#fff"}}></th>
                    <th style={{width:"1rem", backgroundColor:"#fff"}}></th>
                </thead>
                <tbody>
                    {phl.map((entry, index) => (
                        <tr key={index}>
                            <td>{entry.date}</td>
                            <td>{entry.name_address}</td>
                            <td>{entry.total_hours}</td>
                            <td><FontAwesomeIcon icon={faPenToSquare} style={{fontSize:"0.8rem"}} onClick={() => populateFormForEdit(entry)}/></td>
                            <td><FontAwesomeIcon icon={faTrashCan} style={{fontSize:"0.8rem"}} onClick={() => handleDelete(entry.id)}/></td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <br></br>
            <p className="mob-only">For best experience, please use desktop app.</p>
            
            <button className="reval" onClick={() => downloadPHLExcelDirect(phl, profile)}>Download Practice Hours Log</button>
        </div>
        <div className="mob-bottom-buffer">
        </div>
    </div>
  )
}

export default PHL